/**
 * Subservice Form Page (Create/Edit)
 * Handles both regular subservices and internal subservices
 */
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Avatar,
    Typography,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Image as ImageIcon,
    Save as SaveIcon,
} from '@mui/icons-material';
import { PageHeader, ImageSizeHint } from '../../components/common';
import { IMAGE_SIZES } from '../../constants/imageSizes';
import {
    useGetSubServiceQuery,
    useCreateSubServiceMutation,
    useUpdateSubServiceMutation,
    useCreateInternalSubServiceMutation,
    useUpdateInternalSubServiceMutation,
    useGetServicesQuery,
    useGetSubServicesQuery,
    useGetServiceQuery,
} from '../../store/api/servicesApi';
import { InternalSubServiceFormData } from '../../types/service';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const SubserviceFormPage = () => {
    const { t } = useTranslation();
    const { id, parentId } = useParams<{ id?: string; parentId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = !!id;

    // Determine if this is an internal subservice
    // Check if parentId exists in route, or if editing and subservice has parent_id, or if it's standalone create/edit
    const isStandaloneCreate = location.pathname.includes('/internal-subservices/create');
    const isStandaloneEdit = location.pathname.includes('/internal-subservices/') && location.pathname.includes('/edit') && !isStandaloneCreate;
    const isStandalone = isStandaloneCreate || isStandaloneEdit;
    const isInternalSubservice = !!parentId || location.pathname.includes('/internal/') || isStandalone;
    const actualParentId = parentId ? Number(parentId) : undefined;

    const [formData, setFormData] = useState<InternalSubServiceFormData & { service?: number; parent_id?: number }>({
        name: '',
        name_ar: '',
        description: '',
        description_ar: '',
        is_active: true,
        price: '0.00',
        specialist_price: '0.00',
        consultant_price: '0.00',
        service: undefined,
        parent_id: undefined,
    });
    const [iconFile, setIconFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState<string | null>(null);
    const [extraIconFiles, setExtraIconFiles] = useState<File[]>([]);

    const { data: subserviceData, isLoading: isLoadingSubservice } = useGetSubServiceQuery(Number(id!), {
        skip: !isEdit,
    });
    const { data: servicesData } = useGetServicesQuery({});

    // Fetch parent subservice data if creating internal subservice with parentId in URL
    const { data: parentSubserviceData } = useGetSubServiceQuery(actualParentId!, {
        skip: !actualParentId || isEdit || isStandaloneCreate,
    });

    const resolvedServiceId = isInternalSubservice
        ? (formData.service || parentSubserviceData?.data?.service || subserviceData?.data?.service)
        : (formData.service || subserviceData?.data?.service);

    const { data: serviceDetailData } = useGetServiceQuery(Number(resolvedServiceId!), {
        skip: !resolvedServiceId,
    });

    // Fetch subservices for parent selection when creating/editing standalone internal subservice
    const { data: subservicesData, isLoading: isLoadingSubservices } = useGetSubServicesQuery({
        page: 1,
        page_size: 1000,
        service: formData.service || undefined,
    }, {
        skip: (!isStandalone || !formData.service) && !isStandaloneEdit,
    });

    const [createSubService, { isLoading: isCreating }] = useCreateSubServiceMutation();
    const [updateSubService, { isLoading: isUpdating }] = useUpdateSubServiceMutation();
    const [createInternalSubService, { isLoading: isCreatingInternal }] = useCreateInternalSubServiceMutation();
    const [updateInternalSubService, { isLoading: isUpdatingInternal }] = useUpdateInternalSubServiceMutation();

    const selectedService = servicesData?.results?.find((item) => item.id === resolvedServiceId);

    const usesDualPricing = Boolean(
        selectedService?.uses_dual_pricing
        ?? serviceDetailData?.data?.uses_dual_pricing
        ?? subserviceData?.data?.uses_dual_pricing
    );

    // Load subservice data when editing
    useEffect(() => {
        if (isEdit && subserviceData?.data) {
            const subservice = subserviceData.data;
            const isInternal = subservice.is_internal || !!subservice.parent_id;

            if (isInternal) {
                setFormData({
                    name: subservice.name,
                    name_ar: subservice.name_ar || '',
                    description: subservice.description,
                    description_ar: subservice.description_ar || '',
                    is_active: subservice.is_active,
                    price: subservice.price || '0.00',
                    specialist_price: subservice.specialist_price || '0.00',
                    consultant_price: subservice.consultant_price || '0.00',
                    service: subservice.service,
                    parent_id: subservice.parent_id || undefined,
                });
            } else {
                setFormData({
                    service: subservice.service,
                    name: subservice.name,
                    name_ar: subservice.name_ar || '',
                    description: subservice.description,
                    description_ar: subservice.description_ar || '',
                    is_active: subservice.is_active,
                    price: subservice.price || '0.00',
                    specialist_price: subservice.specialist_price || '0.00',
                    consultant_price: subservice.consultant_price || '0.00',
                });
            }
            setIconPreview(subservice.icon || null);
        } else if (actualParentId && parentSubserviceData?.data && !isStandalone) {
            // Pre-fill service from parent when creating internal subservice (not standalone)
            setFormData((prev) => ({
                ...prev,
                service: parentSubserviceData.data.service,
                parent_id: actualParentId,
            }));
        }
    }, [isEdit, subserviceData, actualParentId, parentSubserviceData, isStandalone]);

    const handleInputChange = (field: keyof typeof formData, value: string | number | boolean | undefined) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setIconFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setIconPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleExtraIconsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            setExtraIconFiles(Array.from(files));
        } else {
            setExtraIconFiles([]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const formDataToSend = new FormData();

            if (isInternalSubservice) {
                // Internal subservice fields
                formDataToSend.append('name', formData.name);
                if (formData.name_ar) {
                    formDataToSend.append('name_ar', formData.name_ar);
                }
                formDataToSend.append('description', formData.description || '');
                if (formData.description_ar) {
                    formDataToSend.append('description_ar', formData.description_ar);
                }
                if (usesDualPricing) {
                    formDataToSend.append('specialist_price', formData.specialist_price || '0');
                    formDataToSend.append('consultant_price', formData.consultant_price || '0');
                } else {
                    formDataToSend.append('price', formData.price || '0');
                }
                formDataToSend.append('is_active', formData.is_active.toString());

                if (iconFile) {
                    formDataToSend.append('icon', iconFile);
                }

                // Additional icon files (icon_gallery) – send as multi-file field
                if (extraIconFiles.length > 0) {
                    extraIconFiles.forEach((file) => {
                        formDataToSend.append('icon_gallery', file);
                    });
                }

                // Get parent subservice ID - from form selection (standalone) or URL (nested) or from existing data (edit)
                const subserviceId = isEdit
                    ? (subserviceData?.data?.parent_id || actualParentId || formData.parent_id)!
                    : (isStandalone ? formData.parent_id! : actualParentId!);

                if (!subserviceId) {
                    toast.error(t('subservices.toast.selectParent'));
                    return;
                }

                if (isEdit) {
                    await updateInternalSubService({
                        subservice_id: subserviceId,
                        id: Number(id!),
                        data: formDataToSend
                    }).unwrap();
                    toast.success(t('subservices.toast.internalUpdated'));
                    // Redirect to internal subservice detail if standalone edit, otherwise to parent detail
                    navigate(isStandaloneEdit ? `/internal-subservices/${id}` : `/subservices/${subserviceId}`);
                } else {
                    await createInternalSubService({
                        subservice_id: subserviceId,
                        data: formDataToSend
                    }).unwrap();
                    toast.success(t('subservices.toast.internalCreated'));
                    // Redirect to internal subservices list if standalone, otherwise to parent detail
                    navigate(isStandalone ? '/internal-subservices' : `/subservices/${subserviceId}`);
                }
            } else {
                // Regular subservice fields
                if (formData.service) {
                    formDataToSend.append('service', formData.service.toString());
                }
                formDataToSend.append('name', formData.name);
                formDataToSend.append('name_ar', formData.name_ar ?? '');
                formDataToSend.append('description', formData.description ?? '');
                formDataToSend.append('description_ar', formData.description_ar ?? '');
                if (usesDualPricing) {
                    formDataToSend.append('specialist_price', formData.specialist_price || '0');
                    formDataToSend.append('consultant_price', formData.consultant_price || '0');
                } else {
                    formDataToSend.append('price', formData.price || '0');
                }
                formDataToSend.append('is_active', formData.is_active.toString());
                if (iconFile) {
                    formDataToSend.append('icon', iconFile);
                }

                if (isEdit) {
                    await updateSubService({ id: Number(id!), data: formDataToSend }).unwrap();
                    toast.success(t('subservices.toast.subserviceUpdated'));
                } else {
                    await createSubService(formDataToSend).unwrap();
                    toast.success(t('subservices.toast.subserviceCreated'));
                }
                navigate('/subservices');
            }
        } catch (err: any) {
            const fallback = isInternalSubservice
                ? (isEdit ? t('subservices.toast.internalUpdateFailed') : t('subservices.toast.internalCreateFailed'))
                : (isEdit ? t('subservices.toast.subserviceUpdateFailed') : t('subservices.toast.subserviceCreateFailed'));
            toast.error(err?.data?.message || fallback);
        }
    };

    if (isEdit && isLoadingSubservice) {
        return (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    const isLoading = isCreating || isUpdating || isCreatingInternal || isUpdatingInternal;
    const pageTitle = isInternalSubservice
        ? (isEdit ? t('subservices.form.editInternalTitle') : t('subservices.form.createInternalTitle'))
        : (isEdit ? t('subservices.form.editTitle') : t('subservices.form.createTitle'));

    return (
        <Box sx={{ p: 3 }}>
            <PageHeader
                title={pageTitle}
                breadcrumbs={
                    isStandalone
                        ? [
                            { label: t('nav.dashboard'), path: '/' },
                            { label: t('nav.internalSubServices'), path: '/internal-subservices' },
                            ...(isEdit && id ? [{ label: subserviceData?.data?.name || t('subservices.form.details'), path: `/internal-subservices/${id}` }] : []),
                            { label: isEdit ? t('common.edit') : t('common.create') },
                        ]
                        : [
                            { label: t('nav.dashboard'), path: '/' },
                            { label: isInternalSubservice ? t('nav.internalSubServices') : t('nav.subServices'), path: isInternalSubservice ? '/internal-subservices' : '/subservices' },
                            { label: isEdit ? t('common.edit') : t('common.create') },
                        ]
                }
            />

            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                    if (isStandalone) {
                        if (isEdit) {
                            navigate(`/internal-subservices/${id}`);
                        } else {
                            navigate('/internal-subservices');
                        }
                    } else if (isInternalSubservice && actualParentId) {
                        navigate(`/subservices/${actualParentId}`);
                    } else {
                        navigate('/subservices');
                    }
                }}
                sx={{ mb: 3 }}
            >
                {isStandalone
                    ? (isEdit ? t('subservices.buttons.backToInternalDetails') : t('subservices.buttons.backToInternal'))
                    : isInternalSubservice ? t('subservices.buttons.backToSubserviceDetails') : t('subservices.buttons.backToSubservices')}
            </Button>

            <Card>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Icon Preview/Upload */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    {isInternalSubservice ? t('subservices.form.internalIconTitle') : t('subservices.form.iconTitle')}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar
                                            src={iconPreview || undefined}
                                            variant="rounded"
                                            sx={{ width: 120, height: 120 }}
                                        >
                                            <ImageIcon sx={{ fontSize: 60 }} />
                                        </Avatar>
                                        <Button variant="outlined" component="label" size="medium" disabled={isLoading}>
                                            {iconPreview ? t('subservices.buttons.changeIcon') : t('subservices.buttons.uploadIcon')}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={handleIconChange}
                                            />
                                        </Button>
                                        {iconPreview && (
                                            <Button
                                                variant="text"
                                                color="error"
                                                size="small"
                                                disabled={isLoading}
                                                onClick={() => {
                                                    setIconFile(null);
                                                    setIconPreview(isEdit && subserviceData?.data?.icon ? subserviceData.data.icon : null);
                                                }}
                                            >
                                                {t('subservices.buttons.remove')}
                                            </Button>
                                        )}
                                    </Box>

                                    {isInternalSubservice && (
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            size="medium"
                                            disabled={isLoading}
                                        >
                                            {extraIconFiles.length
                                                ? t('subservices.form.additionalIconsSelected', { num: extraIconFiles.length })
                                                : t('subservices.buttons.uploadAdditionalIcons')}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                multiple
                                                onChange={handleExtraIconsChange}
                                            />
                                        </Button>
                                    )}
                                </Box>
                                <ImageSizeHint size={IMAGE_SIZES.serviceIcon} />
                            </Box>

                            {/* Service Selection - Only for regular subservices or standalone internal subservices */}
                            {(!isInternalSubservice || isStandalone) && (
                                <FormControl fullWidth required>
                                    <InputLabel>{t('subservices.form.service')}</InputLabel>
                                    <Select
                                        value={formData.service || ''}
                                        label={t('subservices.form.service')}
                                        onChange={(e) => {
                                            handleInputChange('service', Number(e.target.value));
                                            // Reset parent when service changes (only on create)
                                            if (isStandaloneCreate) {
                                                handleInputChange('parent_id', undefined);
                                            }
                                        }}
                                        disabled={isLoading || (isEdit && !isStandaloneCreate)}
                                    >
                                        {servicesData?.results?.map((service) => (
                                            <MenuItem key={service.id} value={service.id}>
                                                {service.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            {/* Parent Subservice Selection - Only for standalone internal subservice create/edit */}
                            {isStandalone && formData.service && (
                                <FormControl fullWidth required>
                                    <InputLabel>{t('subservices.form.parentSubservice')}</InputLabel>
                                    <Select
                                        value={formData.parent_id || ''}
                                        label={t('subservices.form.parentSubservice')}
                                        onChange={(e) => handleInputChange('parent_id', e.target.value ? Number(e.target.value) : undefined)}
                                        disabled={isLoading || isLoadingSubservices || isEdit}
                                    >
                                        {isLoadingSubservices ? (
                                            <MenuItem disabled>
                                                <CircularProgress size={20} sx={{ mr: 1 }} />
                                                {t('subservices.form.loadingSubservices')}
                                            </MenuItem>
                                        ) : (
                                            (subservicesData?.results || [])
                                                .filter((s) => !s.parent_id) // Only top-level subservices
                                                .map((subservice) => (
                                                    <MenuItem key={subservice.id} value={subservice.id}>
                                                        {subservice.name}
                                                    </MenuItem>
                                                ))
                                        )}
                                    </Select>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                                        {isEdit ? t('subservices.form.parentCannotChange') : t('subservices.form.parentSelectHint')}
                                    </Typography>
                                </FormControl>
                            )}

                            {/* Parent Info - Only for internal subservices (nested creation) */}
                            {isInternalSubservice && !isStandaloneCreate && parentSubserviceData?.data && (
                                <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        {t('subservices.form.parentSubservice')}
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {parentSubserviceData.data.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {t('subservices.form.serviceColon', { name: parentSubserviceData.data.service_name })}
                                    </Typography>
                                </Box>
                            )}

                            {/* Name */}
                            <TextField
                                fullWidth
                                label={t('subservices.form.name')}
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                required
                                disabled={isLoading}
                            />

                            {/* Name (Arabic) */}
                            <TextField
                                fullWidth
                                label={t('subservices.form.nameAr')}
                                value={formData.name_ar}
                                onChange={(e) => handleInputChange('name_ar', e.target.value)}
                                disabled={isLoading}
                                inputProps={{ dir: 'rtl' }}
                            />

                            {/* Description (English) */}
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={t('subservices.form.descriptionEn')}
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                required
                                disabled={isLoading}
                            />

                            {/* Description (Arabic) */}
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={t('subservices.form.descriptionAr')}
                                value={formData.description_ar}
                                onChange={(e) => handleInputChange('description_ar', e.target.value)}
                                disabled={isLoading}
                                inputProps={{ dir: 'rtl' }}
                            />

                            {/* Pricing */}
                            {usesDualPricing ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('subservices.form.specialistPrice')}
                                        value={formData.specialist_price}
                                        onChange={(e) => handleInputChange('specialist_price', e.target.value)}
                                        required
                                        disabled={isLoading}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">EGP</InputAdornment>,
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('subservices.form.consultantPrice')}
                                        value={formData.consultant_price}
                                        onChange={(e) => handleInputChange('consultant_price', e.target.value)}
                                        required
                                        disabled={isLoading}
                                        inputProps={{ min: 0, step: 0.01 }}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">EGP</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            ) : (
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={t('subservices.form.price')}
                                    value={formData.price}
                                    onChange={(e) => handleInputChange('price', e.target.value)}
                                    required
                                    disabled={isLoading}
                                    inputProps={{ min: 0, step: 0.01 }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">EGP</InputAdornment>,
                                    }}
                                />
                            )}

                            {/* Active Status */}
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_active}
                                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                        disabled={isLoading}
                                    />
                                }
                                label={t('subservices.form.active')}
                            />

                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        if (isStandalone) {
                                            if (isEdit) {
                                                navigate(`/internal-subservices/${id}`);
                                            } else {
                                                navigate('/internal-subservices');
                                            }
                                        } else if (isInternalSubservice && actualParentId) {
                                            navigate(`/subservices/${actualParentId}`);
                                        } else {
                                            navigate('/subservices');
                                        }
                                    }}
                                    disabled={isLoading}
                                >
                                    {t('common.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={
                                        isLoading ||
                                        !formData.name ||
                                        !formData.description ||
                                        (!isInternalSubservice && !formData.service) ||
                                        (isInternalSubservice && isStandalone && (!formData.service || !formData.parent_id)) ||
                                        (isInternalSubservice && !isStandalone && !actualParentId) ||
                                        (usesDualPricing
                                            ? !formData.specialist_price || !formData.consultant_price
                                            : !formData.price)
                                    }
                                >
                                    {isLoading
                                        ? t('subservices.form.saving')
                                        : isEdit
                                            ? (isInternalSubservice ? t('subservices.buttons.updateInternal') : t('subservices.buttons.updateSubservice'))
                                            : (isInternalSubservice ? t('subservices.buttons.createInternal') : t('subservices.buttons.createSubservice'))}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SubserviceFormPage;
